import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFirestore, doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { LanguageSelector } from '../components/LanguageSelector';
import { HoneycombBackground } from '../components/Auth/HoneycombBackground';

interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
}

interface FormStep {
  id: string;
  title: string;
  description: string;
  fields: FormField[];
}

interface QuotationForm {
  businessId: string;
  steps: FormStep[];
  createdAt: Date;
  updatedAt: Date;
}

const PublicQuotationForm: React.FC = () => {
  const { t } = useTranslation();
  const { businessId } = useParams<{ businessId: string }>();
  const [formData, setFormData] = useState<QuotationForm | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string | string[]>>({});
  const [loading, setLoading] = useState(true);
  const [businessLogo, setBusinessLogo] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadFormData = async () => {
      if (!businessId) return;
      
      try {
        const db = getFirestore();
        const formDoc = await getDoc(doc(db, 'QuotationForms', businessId));
        
        if (formDoc.exists()) {
          const data = formDoc.data() as QuotationForm;
          setFormData(data);
        }
      } catch (error) {
        console.error('Error loading form:', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    const fetchBusinessData = async () => {
      if (!businessId) return;
      
      try {
        const db = getFirestore();
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          const businessData = businessDoc.data();
          if (businessData.logo) {
            setBusinessLogo(businessData.logo);
          }
        }
      } catch (error) {
        console.error('Error fetching business data:', error);
      }
    };

    loadFormData();
    fetchBusinessData();
  }, [businessId, t]);

  const handleInputChange = (fieldId: string, value: string | string[]) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!businessId || !formData) return;

    try {
      const db = getFirestore();
      const submissionsRef = collection(db, 'quotations', businessId, 'submissions');

      // Separar campos fijos y personalizados
      const fixedFields = ['clientName', 'clientEmail'];
      const fixedData: Record<string, string> = {};
      const customFields: { id: string; label: string; value: string | string[] }[] = [];

      formData.steps.forEach(step => {
        step.fields.forEach(field => {
          const value = formValues[field.id] || '';
          if (fixedFields.includes(field.id)) {
            fixedData[field.id] = typeof value === 'string' ? value : value.join(',');
          } else {
            if (field.type === 'multiselect') {
              customFields.push({ id: field.id, label: field.label, value: Array.isArray(value) ? value : value ? value.split(',') : [] });
            } else {
              customFields.push({ id: field.id, label: field.label, value: typeof value === 'string' ? value : value.join(',') });
            }
          }
        });
      });

      await addDoc(submissionsRef, {
        ...fixedData,
        customFields,
        createdAt: new Date(),
        status: 'unread'
      });

      // Redirigir a la página de agradecimiento
      navigate('/thank-you');
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error(t('error.submittingForm'));
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 relative">
        <div className="w-full max-w-4xl bg-white rounded-xl shadow-sm p-8 relative">
          <div className="text-center text-lg text-gray-500">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!formData || !formData.steps || formData.steps.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 relative">
        <div className="w-full max-w-4xl bg-white rounded-xl shadow-sm p-8 relative">
          <div className="text-center text-lg text-gray-500">{t('common.formNotFound')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 relative">
      <HoneycombBackground />
      <LanguageSelector />
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-sm p-8 relative">
        <div className="flex items-center gap-4 mb-8">
          {businessLogo && (
            <img 
              src={businessLogo} 
              alt="Business Logo" 
              className="w-10 h-10 rounded-lg object-cover"
            />
          )}
          <h1 className="text-2xl font-bold text-gray-900 m-0">
            {t('quotations.formBuilder.title')}
          </h1>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {formData.steps.map((step, stepIndex) => (
            <div key={step.id} className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {stepIndex + 1}. {step.title}
              </h2>
              {step.description && (
                <p className="text-gray-600 mb-6 text-base">{step.description}</p>
              )}
              <div className="mb-6">
                {step.fields.map((field) => (
                  <div key={field.id} className="flex flex-col gap-2 mb-6">
                    <label className="text-gray-900 font-medium">
                      {field.label}
                      {field.required && <span className="text-red-600 ml-1">*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={formValues[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        required={field.required}
                        placeholder={field.placeholder}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-base min-h-24 resize-vertical transition-colors duration-200 focus:outline-none focus:border-red-600"
                      />
                    ) : field.type === 'select' ? (
                      <select
                        value={formValues[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        required={field.required}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600 bg-white"
                      >
                        <option value="">{t('common.select')}</option>
                        {field.options?.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'multiselect' ? (
                      <select
                        multiple
                        value={Array.isArray(formValues[field.id]) ? formValues[field.id] : (formValues[field.id] ? (formValues[field.id] as string).split(',') : [])}
                        onChange={e => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          handleInputChange(field.id, selected);
                        }}
                        required={field.required}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600 bg-white"
                      >
                        {field.options?.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        value={formValues[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        required={field.required}
                        placeholder={field.placeholder}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          <button 
            type="submit" 
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white border-none px-6 py-3 rounded-lg cursor-pointer text-base font-medium transition-colors duration-200"
          >
            {isLoading ? t('common.submitting') : t('common.submit')}
          </button>
        </form>
        
        <div className="mt-12 text-center p-4 flex flex-col items-center gap-2">
          <span className="text-gray-600 text-sm">powered by</span>
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54" 
            alt="Brix Logo"
            className="h-8 w-auto"
          />
        </div>
      </div>
    </div>
  );
};

export default PublicQuotationForm; 